import React from 'react';
import { Dropdown, Menu, Popconfirm, Tag, Tooltip } from 'antd';
import { DeleteOutlined, MoreOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import type { OoReport, Report } from '../../types';
import { getStatusStyle } from './statusStyles';

interface ReportHistoryProps {
  reports: Report[];
  /** doc_id currently open in the editor — used to highlight the loaded row */
  activeDocId?: number | null;
  onLoad: (report: Report) => void;
  onDelete: (report: Report) => void;
}

const ReportHistory: React.FC<ReportHistoryProps> = ({ reports, activeDocId, onLoad, onDelete }) => {
  if (!reports.length) {
    return <div className="no-content-message">No reports available</div>;
  }

  return (
    <div className="report-history-list">
      {reports.map(record => {
        const rec = record as OoReport;
        const docId = rec.document?.doc_id;
        const isActive = !!docId && docId === activeDocId;
        const isSigned = record.pr_status === 'SIGNEDOFF';

        const menu = (
          <Menu>
            <Menu.Item key="load" icon={<EyeOutlined />} onClick={() => onLoad(record)}>
              {isSigned ? 'View' : 'Open'}
            </Menu.Item>
            <Menu.Item key="delete" icon={<DeleteOutlined />} disabled={isSigned}>
              <Popconfirm
                title="Delete report?"
                description="This will permanently delete the report and its document. This action cannot be undone."
                onConfirm={() => onDelete(record)}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={isSigned}
              >
                Delete
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <div
            key={record.pr_id}
            className={`report-history-row${isActive ? ' active' : ''}`}
            onClick={() => onLoad(record)}
            title={isSigned ? 'View this signed report' : 'Open this draft'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              marginBottom: 4,
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              borderLeft: `3px solid ${isActive ? '#1677ff' : 'transparent'}`,
              background: isActive ? '#E6F4FF' : 'transparent',
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Line 1: status + user */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <Tag
                  style={{ ...getStatusStyle(record.pr_status), margin: 0, fontSize: 11, lineHeight: '18px' }}
                >
                  {record.pr_status?.replaceAll('_', ' ')}
                </Tag>
                <span
                  style={{
                    color: '#334155',
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {record.pr_reported_by || '—'}
                </span>
              </div>
              {/* Line 2: datetime + loaded indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' }}>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {moment(record.createdAt).format('DD-MM-YY hh:mm A')}
                </span>
                {isActive && (
                  <span style={{ color: '#1677ff', fontWeight: 600, whiteSpace: 'nowrap' }}>● loaded</span>
                )}
              </div>
            </div>
            <Tooltip title="Actions">
              <Dropdown overlay={menu} trigger={['click']}>
                <MoreOutlined
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: '1rem', cursor: 'pointer' }}
                />
              </Dropdown>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

export default ReportHistory;
